<template>
  <section
    ref="sectionEl"
    class="py-24 bg-white dark:bg-dark transition-colors duration-300"
    :class="{ 'is-visible': isVisible }"
  >
    <div class="container mx-auto px-4">
      <div class="max-w-3xl mx-auto text-center mb-14">
        <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">
          Recensioni
        </p>
        <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
          Cosa dicono i nostri clienti
        </h2>
        <p class="text-lg text-gray-500 dark:text-gray-400">
          Risultati reali per imprenditori e aziende in Ticino e in tutta la Svizzera.
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
        <div
          v-for="(review, idx) in reviews"
          :key="`${review.name}-${idx}`"
          class="rounded-2xl border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-6 flex flex-col anim-el"
          :class="'d' + (idx + 1)"
        >
          <div class="flex gap-0.5 mb-3">
            <Star
              v-for="i in 5"
              :key="i"
              class="h-4 w-4"
              :class="i <= review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 dark:text-gray-600'"
            />
          </div>

          <span
            v-if="review.tag"
            class="inline-block self-start px-2.5 py-1 rounded-md bg-blue-500/10 text-blue-500 text-[11px] font-semibold tracking-wide mb-4"
          >
            {{ review.tag }}
          </span>

          <p class="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-5 flex-1">
            <span class="line-clamp-8">
              "{{ review.quote }}"
            </span>
          </p>

          <div class="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-white/[0.06]">
            <div class="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img
                v-if="review.avatarUrl"
                :src="review.avatarUrl"
                :alt="review.name"
                class="w-full h-full object-cover"
                loading="lazy"
              />
              <User v-else class="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ review.name }}</p>
              <p class="text-xs text-gray-400 dark:text-gray-500">{{ review.role }}</p>
            </div>
          </div>
        </div>
      </div>

      <p v-if="!reviews.length && !isLoading" class="text-center mt-6 text-sm text-gray-500 dark:text-gray-400">
        Nessuna testimonianza disponibile al momento.
      </p>

      <div class="flex flex-wrap justify-center gap-8 mt-12">
        <div class="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <div class="flex gap-0.5">
            <Star v-for="i in 5" :key="i" class="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
          </div>
          <span><span class="font-bold text-gray-900 dark:text-white">4.9/5</span> media recensioni</span>
        </div>
        <div class="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Check class="h-4 w-4 text-green-500" />
          <span><span class="font-bold text-gray-900 dark:text-white">100%</span> progetti consegnati</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Star, Check, User } from 'lucide-vue-next'

const { locale } = useI18n()
const sectionEl = ref(null)
const isVisible = ref(false)
const isLoading = ref(false)
const reviews = ref([])
let observer = null

const trackingCode = 'AVElLWEEFS1qC3341qy6mQ'
const apiUrl = `https://panettone.ciobai.ch/analytics/api/public/testimonials?tracking_code=${trackingCode}&limit=6`

const getLocalizedText = (item, itField, enField) => {
  if ((locale.value || '').startsWith('en')) {
    return item?.[enField] || item?.[itField] || ''
  }
  return item?.[itField] || item?.[enField] || ''
}

const mapReview = (item) => ({
  tag: item.tag || '',
  quote: getLocalizedText(item, 'text', 'text_en'),
  name: item.name || 'Cliente',
  role: getLocalizedText(item, 'role', 'role_en'),
  rating: Math.max(0, Math.min(5, Number(item.rating) || 0)),
  avatarUrl: item.avatar_url || ''
})

const fetchReviews = async () => {
  isLoading.value = true
  try {
    const response = await fetch(apiUrl)
    const json = await response.json()
    reviews.value = Array.isArray(json?.data) ? json.data.map(mapReview) : []
  } catch (error) {
    console.error('Testimonials API error:', error)
    reviews.value = []
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) isVisible.value = true
    })
  }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' })
  if (sectionEl.value) observer.observe(sectionEl.value)
  fetchReviews()
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})
</script>

<style scoped>
.anim-el {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.7s cubic-bezier(.22,1,.36,1), transform 0.7s cubic-bezier(.22,1,.36,1);
}
.is-visible .anim-el { opacity: 1; transform: translateY(0); }
.d1 { transition-delay: 0.05s; }
.d2 { transition-delay: 0.12s; }
.d3 { transition-delay: 0.19s; }
.d4 { transition-delay: 0.26s; }
.d5 { transition-delay: 0.33s; }
.d6 { transition-delay: 0.40s; }

.line-clamp-8 {
  display: -webkit-box;
  -webkit-line-clamp: 8;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
